/**
 * Stage Plot API client
 *
 * Thin fetch wrapper around the backend stage plot CRUD endpoints.
 * Uses relative URLs (/api/stageplots) — the Vite dev server proxies
 * to port 3099; production nginx handles the proxy.
 */

import type { StagePlot } from '../shared/types/stageplot';

// ──────────────────────────────────────────
// Types
// ──────────────────────────────────────────

export interface CreateStagePlotPayload {
  name: string;
  riderId?: string;
  bandId?: string;
  data: StagePlot;
}

export interface UpdateStagePlotPayload {
  name?: string;
  riderId?: string | null;
  bandId: string;
  data?: StagePlot;
}

export interface StagePlotListItem {
  id: string;
  name: string;
  riderId: string | null;
  createdAt: string;
  updatedAt: string;
  data: StagePlot;
}

export interface PaginatedResponse {
  items: StagePlotListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ApiError {
  error: string;
  details?: Record<string, string[]>;
}

// ──────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────

class StagePlotApiError extends Error {
  status: number;
  details?: Record<string, string[]>;

  constructor(message: string, status: number, details?: Record<string, string[]>) {
    super(message);
    this.name = 'StagePlotApiError';
    this.status = status;
    this.details = details;
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let body: ApiError | null = null;
    try {
      body = (await response.json()) as ApiError;
    } catch {
      // response body not JSON
    }
    throw new StagePlotApiError(
      body?.error ?? `Request failed with status ${response.status}`,
      response.status,
      body?.details,
    );
  }

  // 204 No Content (DELETE)
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

// ──────────────────────────────────────────
// Operations
// ──────────────────────────────────────────

/** Create a new stage plot */
export async function createStagePlot(
  payload: CreateStagePlotPayload,
): Promise<StagePlotListItem> {
  const res = await fetch('/api/stageplots', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  return handleResponse<StagePlotListItem>(res);
}

/** Fetch a single stage plot by ID */
export async function getStagePlot(id: string, bandId: string): Promise<StagePlotListItem> {
  const res = await fetch(`/api/stageplots/${encodeURIComponent(id)}?bandId=${encodeURIComponent(bandId)}`, {
    method: 'GET',
    credentials: 'include',
  });
  return handleResponse<StagePlotListItem>(res);
}

/** Update an existing stage plot (partial update) */
export async function updateStagePlot(
  id: string,
  payload: UpdateStagePlotPayload,
): Promise<StagePlotListItem> {
  const res = await fetch(`/api/stageplots/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  return handleResponse<StagePlotListItem>(res);
}

/** Delete a stage plot */
export async function deleteStagePlot(id: string, bandId: string): Promise<void> {
  const res = await fetch(`/api/stageplots/${encodeURIComponent(id)}?bandId=${encodeURIComponent(bandId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  return handleResponse<void>(res);
}

/** List stage plots with optional riderId filter and pagination */
export async function listStagePlots(params?: {
  page?: number;
  limit?: number;
  riderId?: string;
  bandId: string;
}): Promise<PaginatedResponse> {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.riderId) query.set('riderId', params.riderId);
  if (params?.bandId) query.set('bandId', params.bandId);

  const qs = query.toString();
  const url = qs ? `/api/stageplots?${qs}` : '/api/stageplots';

  const res = await fetch(url, { method: 'GET', credentials: 'include' });
  return handleResponse<PaginatedResponse>(res);
}

export { StagePlotApiError };
